import { useState, useMemo } from "react";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PackageBox from "@/components/PackageBox";
import ProductSelector from "@/components/ProductSelector";
import { products, priceTiers, getExactTier, DRINKS_PER_TUBE, type OrderMode } from "@/data/pricing";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionAdjusterProps {
  subscriptionId: string;
  initialQuantities: Record<string, number>;
  initialTubes: number;
  pendingEffectiveDate?: string | null;
  mode?: OrderMode;
  onBack: () => void;
  onSaved: (effectiveDate: string) => void;
}


const SubscriptionAdjuster = ({
  subscriptionId,
  initialQuantities,
  initialTubes,
  pendingEffectiveDate,
  mode = "subscription",
  onBack,
  onSaved,
}: SubscriptionAdjusterProps) => {
  const [quantities, setQuantities] = useState<Record<string, number>>(initialQuantities);
  const [celebrationKey, setCelebrationKey] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const [celebratedTier, setCelebratedTier] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const totalQty = useMemo(
    () => Object.values(quantities).reduce((sum, q) => sum + q, 0),
    [quantities]
  );

  const maxTubes = priceTiers[priceTiers.length - 1].tubes;
  const currentTier = getExactTier(totalQty);
  const price = currentTier
    ? mode === "subscription"
      ? currentTier.subscriptionPrice
      : currentTier.purchasePrice
    : null;

  const triggerCelebration = (tubes: number) => {
    if (celebratedTier === tubes) return;
    setCelebratedTier(tubes);
    setCelebrating(true);
    setCelebrationKey((k) => k + 1);
    setTimeout(() => setCelebrating(false), 2000);
  };

  const handleAdd = (productId: string) => {
    if (totalQty >= maxTubes) return;
    setQuantities((prev) => {
      const next = { ...prev, [productId]: (prev[productId] || 0) + 1 };
      const newTotal = Object.values(next).reduce((s, q) => s + q, 0);
      const tier = priceTiers.find((t) => t.tubes === newTotal);
      if (tier) triggerCelebration(tier.tubes);
      return next;
    });
  };

  const handleRemove = (productId: string) => {
    setQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current === 0) return prev;
      const next = { ...prev, [productId]: current - 1 };
      const newTotal = Object.values(next).reduce((s, q) => s + q, 0);
      if (celebratedTier && newTotal < celebratedTier) setCelebratedTier(null);
      return next;
    });
  };

  const handleSave = async () => {
    if (!currentTier) {
      toast.error("Välj en giltig paketstorlek först");
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: {
          subscription_id: subscriptionId,
          new_quantities: quantities,
          new_tier_tubes: currentTier.tubes,
        },
      });
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Kunde inte spara");
      }
      const formatted = new Date(data.effective_date).toLocaleDateString("sv-SE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      toast.success(`Justeringen träder i kraft från leveransen ${formatted}`);
      onSaved(data.effective_date);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const totalDrinks = totalQty * DRINKS_PER_TUBE;
  const hasChanges = JSON.stringify(quantities) !== JSON.stringify(initialQuantities);

  return (
    <section className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Tillbaka till konto
        </button>

        <div className="mb-10">
          <span className="font-serif italic text-2xl text-foreground">Justera</span>
          <h1 className="text-lg font-bold uppercase tracking-wider text-foreground">Ditt paket</h1>
        </div>

        {pendingEffectiveDate && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
            Du har redan en planerad justering till{" "}
            <strong>
              {new Date(pendingEffectiveDate).toLocaleDateString("sv-SE", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </strong>
            . Sparar du nu ersätts den.
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: animated box */}
          <div className="flex justify-center lg:sticky lg:top-24">
            <PackageBox
              quantities={quantities}
              products={products}
              totalQty={totalQty}
              currentTierTubes={celebratedTier}
              celebrating={celebrating}
              celebrationKey={celebrationKey}
              mode={mode}
            />
          </div>

          {/* Right: selectors + summary */}
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-3">
              {products.map((product) => (
                <ProductSelector
                  key={product.id}
                  product={product}
                  quantity={quantities[product.id] || 0}
                  onAdd={() => handleAdd(product.id)}
                  onRemove={() => handleRemove(product.id)}
                  disabled={totalQty >= maxTubes}
                />
              ))}
            </div>

            {/* Summary */}
            <div className="bg-card rounded-2xl p-6 space-y-4">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Totalt</p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {totalQty} <span className="text-sm font-normal text-muted-foreground">tuber</span>
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    {totalDrinks} drycker
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {currentTier ? `${currentTier.name} paket` : "Välj storlek"}
                  </p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">
                    {price ? `${price} kr` : "—"}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    per månad
                  </p>
                </div>
              </div>

              {!currentTier && totalQty > 0 && (
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground border-t border-border pt-3">
                  Välj en giltig paketstorlek för att fortsätta
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSave}
                disabled={!currentTier || !hasChanges || saving}
                className="flex-1 bg-foreground text-background py-3.5 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground/90 transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {saving ? "Sparar…" : "Spara ändringar"}
              </button>
              <button
                onClick={onBack}
                disabled={saving}
                className="border border-foreground text-foreground bg-transparent py-3.5 px-6 rounded-lg font-medium uppercase tracking-widest text-[10px] hover:bg-foreground hover:text-background transition-all duration-300 disabled:opacity-30"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionAdjuster;
